import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import WomLogoAnimation from './WomLogoAnimation';
import SplashScreenAds from './SplashScreenAds';
import useUser from '../../hooks/useUser';
import { useGlobalData } from '../../contextProvider/GlobalDataContext';
import { getSplashScreenAds } from '../../services/Contentful';

const MIN_LOADING_TIME_STAGE_1 = 3000;
const SPLASH_SCREEN = {
  STAGE_1: 1, // WOM logo
  WAITING_FOR_ADS: 1.5, // Time is up, but still waiting for contentful ads
  STAGE_2: 2, // Ads
  WAITING_FOR_DATA: 2.5, // Time is up, but possibly still waiting for other data
};

const SplashScreen = ({ hideSplashScreen }) => {
  const user = useUser();
  const { loading: globalDataLoading } = useGlobalData();
  const timer1 = useRef(0);
  const timer2 = useRef(0);
  const [stage, setStage] = useState(SPLASH_SCREEN.STAGE_1);
  const [ads, setAds] = useState(null);
  const adsDuration = ads?.duration ?? 3;
  const expirationDate = new Date(ads?.expirationDate ?? null);
  const showAds = ads?.active && Date.now() < expirationDate.getTime();
  const showWomLogo = stage < SPLASH_SCREEN.STAGE_2 || !showAds;
  const loadingAds = ads === null;
  const loadingData = user === null || globalDataLoading;

  const loadAds = async () => {
    try {
      const [adsData] = await getSplashScreenAds();
      const img = new Image();
      img.src = adsData.image.url; // preloads the image
      setAds(adsData);
    } catch (e) {
      setAds({});
    }
  };

  useEffect(() => {
    timer1.current = setTimeout(() => {
      setStage(SPLASH_SCREEN.WAITING_FOR_ADS);
    }, MIN_LOADING_TIME_STAGE_1);
    loadAds();
    return () => clearTimeout(timer1.current);
  }, []);

  useEffect(() => {
    if (stage === SPLASH_SCREEN.WAITING_FOR_ADS && !loadingAds) {
      if (showAds) setStage(SPLASH_SCREEN.STAGE_2);
      else {
        // skip Ads stage
        setStage(SPLASH_SCREEN.WAITING_FOR_DATA);
      }
    }
  }, [stage, ads]);

  useEffect(() => {
    if (stage === SPLASH_SCREEN.STAGE_2) {
      timer2.current = setTimeout(() => {
        setStage(SPLASH_SCREEN.WAITING_FOR_DATA);
      }, adsDuration * 1000);
    }
    return () => clearTimeout(timer2.current);
  }, [stage]);

  useEffect(() => {
    if (stage === SPLASH_SCREEN.WAITING_FOR_DATA && !loadingData) {
      hideSplashScreen();
    }
  }, [stage, loadingData]);

  return showWomLogo ? <WomLogoAnimation /> : <SplashScreenAds ads={ads} />;
};

SplashScreen.propTypes = {
  hideSplashScreen: PropTypes.func.isRequired,
};

export default SplashScreen;
